import { PaymentProviderType } from "@redotech/redo-billing-common/billing-engine-types";
import { getLegacySubscriptionBillingStartDate } from "@redotech/redo-billing-common/outbound-labels/legacy-subscription-billing";
import { BillingPeriod } from "@redotech/redo-billing-common/service-invoice";
import { Settings } from "@redotech/redo-team-common/team";
import { MerchantCarrierFeeSettingsValue } from "./merchant-carrier-fee-settings";

type OutboundLabelsSettings = NonNullable<Settings["outboundLabels"]>;
export type OutboundLabelsFees = NonNullable<OutboundLabelsSettings["fees"]>;
export type WmsFees = NonNullable<
  NonNullable<OutboundLabelsSettings["wms"]>["fees"]
>;
export type OmsSubscriptionBilling = NonNullable<
  OutboundLabelsSettings["subscriptionBilling"]
>;
export type WmsSubscriptionBilling = NonNullable<
  NonNullable<OutboundLabelsSettings["wms"]>["subscriptionBilling"]
>;

/** Mirrors an admin fee edit onto the subdoc the engine actually bills from. */
function buildSubscriptionBillingUpdate(
  existing: OmsSubscriptionBilling | WmsSubscriptionBilling | undefined | null,
  fee: { amount: number; turnedOnAt: Date | string | undefined },
  lastBilledDate: Date | string | undefined | null,
): WmsSubscriptionBilling {
  const turnedOnAt = fee.turnedOnAt ? new Date(fee.turnedOnAt) : undefined;
  return {
    ...existing,
    enabled: !!turnedOnAt && fee.amount > 0,
    startDate:
      existing?.startDate ??
      getLegacySubscriptionBillingStartDate({
        now: new Date(),
        lastBilledDate: lastBilledDate ? new Date(lastBilledDate) : undefined,
        turnedOnAt,
      }),
    period: existing?.period ?? BillingPeriod.MONTHLY,
    periodFeeInCents: Math.round(fee.amount * 100),
    renewOnPeriodEnd: existing?.renewOnPeriodEnd ?? true,
    paymentProvider: existing?.paymentProvider ?? PaymentProviderType.STRIPE,
  };
}

/**
 * Build the `outboundLabels.fees` update from the admin form value while
 * preserving billing-managed fields the UI does not control.
 *
 * Legacy billing stamped `lastBilledDate` on these fee objects as its
 * idempotency guard. Rebuilding the fees object from scratch on every
 * settings save wiped those timestamps and double-charged teams. Spreading
 * the existing values keeps the UI in control of amounts/`turnedOnAt`
 * without clobbering billing state.
 */
export function buildOutboundLabelsFeesUpdate(
  existing: OutboundLabelsFees | undefined,
  value: MerchantCarrierFeeSettingsValue,
  chargeCreditCardProcessingFee: boolean,
): OutboundLabelsFees {
  return {
    ...existing,
    merchantCarrierAccountLabelFee: {
      ...existing?.merchantCarrierAccountLabelFee,
      amount: value.merchantCarrierAccountLabelFee.amount,
      tiktokShippingAmount:
        value.merchantCarrierAccountLabelFee.tiktokShippingAmount,
    },
    monthlySubscriptionFee: {
      ...existing?.monthlySubscriptionFee,
      amount: value.monthlySubscriptionFee.amount,
      turnedOnAt: value.monthlySubscriptionFee.turnedOnAt,
    },
    chargeCreditCardProcessingFee,
  };
}

/** Keeps `outboundLabels.subscriptionBilling` in step with the OMS fee edit. */
export function buildOmsSubscriptionBillingUpdate(
  existingBilling: OmsSubscriptionBilling | undefined | null,
  existingFees: OutboundLabelsFees | undefined,
  value: MerchantCarrierFeeSettingsValue,
): WmsSubscriptionBilling {
  return buildSubscriptionBillingUpdate(
    existingBilling,
    value.monthlySubscriptionFee,
    existingFees?.monthlySubscriptionFee?.lastBilledDate,
  );
}

/** Keeps `outboundLabels.wms.subscriptionBilling` in step with the WMS fee edit. */
export function buildWmsSubscriptionBillingUpdate(
  existingBilling: WmsSubscriptionBilling | undefined | null,
  existingFees: WmsFees | undefined,
  wmsSubscriptionFee: MerchantCarrierFeeSettingsValue["wmsSubscriptionFee"],
): WmsSubscriptionBilling {
  return buildSubscriptionBillingUpdate(
    existingBilling,
    wmsSubscriptionFee,
    existingFees?.monthlySubscriptionFee?.lastBilledDate,
  );
}

/**
 * Build the `outboundLabels.wms.fees` update from the admin form value,
 * preserving the billing engine's `lastBilledDate` for the same reason as
 * {@link buildOutboundLabelsFeesUpdate}.
 */
export function buildWmsFeesUpdate(
  existing: WmsFees | undefined,
  wmsSubscriptionFee: MerchantCarrierFeeSettingsValue["wmsSubscriptionFee"],
): WmsFees {
  return {
    ...existing,
    monthlySubscriptionFee: {
      ...existing?.monthlySubscriptionFee,
      amount: wmsSubscriptionFee.amount,
      turnedOnAt: wmsSubscriptionFee.turnedOnAt,
    },
  };
}
